// /src/components/AuthProvider.tsx

// What? Handles authentication for the application.
// Why? To manage user authentication and authorization for different environments.
// How?
// - Configures Auth0 or Entra ID for authentication.
// - Provides protected routes for authenticated users.
// - Handles user status checks and redirects for unverified users.

import React, { ReactNode, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useAuth0, Auth0Provider } from "@auth0/auth0-react";
import {
  PublicClientApplication,
  InteractionStatus,
  InteractionType,
  InteractionRequiredAuthError,
  AuthError,
} from "@azure/msal-browser";
import {
  MsalProvider,
  useMsal,
  MsalAuthenticationTemplate,
} from "@azure/msal-react";
import { UnsafeAuthProvider } from "./UnsafeAuthProvider";
import { useReadUser } from "../services/api";
import Unverified from "../views/Unverified";
import NavContainerNoSidebar from "./NavContainerNoSidebar";
import {
  getAuthenticationMethod,
  getRuntimeConfig,
  isLocalhostHostname,
  isMockApiRuntime,
} from "../config/runtimeConfig";
import { getCurrentRelativeUrl } from "../utils/authReturnTarget";

////////////////////
// Configurations //
////////////////////
// EntraID Configuration (TODO: Add to env variables)
export const msalConfig = {
  auth: {
    clientId: "0501f640-94a0-4fd9-a7ae-0e8fa5938130",
    authority: "https://login.microsoftonline.com/common/v2.0",
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

/////////////////////
// Protected Route //
/////////////////////
interface ProtectedRouteAuth0Props {
  children: ReactNode;
}

// What? A protected route for authenticated users.
// Why? To ensure that only authenticated users can access certain parts of the application.
// How?
const ProtectedRouteAuth0: React.FC<ProtectedRouteAuth0Props> = ({
  children,
}) => {
  const location = useLocation();
  const {
    logout,
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
    isLoading,
  } = useAuth0();
  const readUserAPI = useReadUser(); // Hook to fetch user data
  const navigate = useNavigate();
  const [isUserChecked, setIsUserChecked] = useState(false); // Track if user status is checked

  // Ensure valid token
  React.useEffect(() => {
    const login = async () => {
      try {
        await getAccessTokenSilently();
      } catch (error) {
        await loginWithRedirect({
          appState: {
            returnTo: `${location.pathname}${location.search}${location.hash}`,
          },
        });
      }
    };

    if (!isLoading) {
      login();
    }
  }, [
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    location.pathname,
    location.search,
    location.hash,
  ]);

  // Check if verified
  React.useEffect(() => {
    const checkUserStatus = async () => {
      const user = await readUserAPI(); // Fetch user data
      if (user.status === "created") {
        navigate("/unverified"); // Redirect to unverified page if necessary
      }

      setIsUserChecked(true);
    };

    if (isAuthenticated && !isUserChecked) {
      checkUserStatus();
    }
  }, [isAuthenticated, isUserChecked, readUserAPI, navigate]);

  React.useEffect(() => {
    // Handle explicit logout route
    if (location.pathname === "/logout") {
      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
  }, [location, logout]);

  if (isLoading || !isAuthenticated || !isUserChecked) {
    return <div>Loading...</div>;
  }

  return <>{children}</>;
};

interface ProtectedRouteEntraIDProps {
  children: ReactNode;
}

// What? A protected route for authenticated users.
// Why? To ensure that only authenticated users can access certain parts of the application.
// How?
// - Uses the useMsal hook to check if the user is authenticated.
// - Uses the useReadUser hook to fetch user data.
// - If the user is not authenticated, redirects to the login page.
// - If the user is not verified, redirects to the unverified page.
// - Otherwise, renders the component passed as a prop.
const ProtectedRouteEntraID: React.FC<ProtectedRouteEntraIDProps> = ({
  children,
}) => {
  const location = useLocation();
  const { instance, inProgress } = useMsal();

  React.useEffect(() => {
    const logout = async () => {
      if (
        location.pathname === "/logout" &&
        inProgress === InteractionStatus.None
      ) {
        await instance.initialize();
        instance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin,
        });
      }
    };

    logout();
  }, [location, instance, inProgress]);

  if (inProgress !== InteractionStatus.None) {
    return <p>Loading...</p>;
  }

  return <>{children}</>;
};

/////////////////////////////////////
// Authentication Provider Wrapper //
/////////////////////////////////////
interface AuthProviderProps {
  children: ReactNode;
}

// Defense-in-depth surface for AUTHENTICATION_METHOD=UNSAFE on a non-localhost
// host. Per `feedback_never_use_unsafe_auth`, UNSAFE must never accept traffic
// in a deployed environment -- env-var discipline is not enough. Rendered
// instead of UnsafeAuthProvider whenever isLocalhostHostname() is false, so an
// accidental UNSAFE deploy fails loud at the FE rather than silently granting
// access via ?user_name=.
const UnsafeAuthRefusedNotice: React.FC = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      padding: "2rem",
      fontFamily: "system-ui, sans-serif",
      textAlign: "center",
    }}
  >
    <div style={{ maxWidth: 520 }}>
      <h1 style={{ fontSize: "1.5rem", marginBottom: "1rem" }}>
        Authentication misconfigured
      </h1>
      <p style={{ fontSize: "0.95rem", lineHeight: 1.5, color: "#444" }}>
        AUTHENTICATION_METHOD=UNSAFE is only permitted on localhost development
        hosts. Set AUTHENTICATION_METHOD to AUTH0 or ENTRAID for this
        environment and redeploy.
      </p>
    </div>
  </div>
);

// What? An authentication provider wrapper.
// Why? To handle authentication for the application.
// How?
// - Checks the authentication method from environment variables.
// - If the method is Auth0, uses the Auth0Provider.
// - If the method is Entra ID, uses the MsalProvider.
// - Otherwise, uses the UnsafeAuthProvider, but only on localhost hosts.
const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Mock API. Host-gated: mock mode only ever made sense on localhost, but
  // make that explicit so isMockApiRuntime() drift can't reopen the bypass.
  if (isMockApiRuntime()) {
    if (!isLocalhostHostname()) return <UnsafeAuthRefusedNotice />;
    return <UnsafeAuthProvider>{children}</UnsafeAuthProvider>;
  }

  const authenticationMethod = getAuthenticationMethod();

  // Auth0
  if (authenticationMethod === "AUTH0") {
    const onAuth0RedirectCallback = (appState: any) => {
      window.history.replaceState(
        {},
        document.title,
        appState?.returnTo || getCurrentRelativeUrl(),
      );
    };

    const scope: string = "openid profile email offline_access";
    const runtimeConfig = getRuntimeConfig();

    const domain: string = runtimeConfig.auth0Domain;
    const clientId: string = runtimeConfig.auth0ClientId;
    const audience: string = runtimeConfig.auth0Audience;

    return (
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        onRedirectCallback={onAuth0RedirectCallback}
        scope={scope}
        authorizationParams={{
          scope: scope,
          audience: audience,
          redirect_uri: window.location.origin,
        }}
        useRefreshTokens={true}
        useRefreshTokensFallback={true} // No drawback as long as we use custom domains in auth0
        cacheLocation="localstorage" // Ensures persistence across sessions
      >
        <Routes>
          {/* Unverified Route */}
          <Route
            path="/unverified"
            element={
              <NavContainerNoSidebar>
                <Unverified />
              </NavContainerNoSidebar>
            }
          />
          <Route
            path="*"
            element={
              <ProtectedRouteAuth0>
                <>{children}</>
              </ProtectedRouteAuth0>
            }
          />
        </Routes>
      </Auth0Provider>
    );
  }

  // Entra ID
  if (authenticationMethod === "ENTRAID") {
    const msalInstance = new PublicClientApplication(msalConfig);
    const LoadingComponent = () => <p>Loading...</p>;

    const authenticationRequest = {
      scopes: [
        "User.Read",
        "Calendars.ReadWrite",
        "OnlineMeetings.Read",
        "OnlineMeetingTranscript.Read.All",
        "openid",
        "profile",
      ],
    };

    interface ErrorComponentProps {
      error: AuthError | null;
    }

    const ErrorComponent: React.FC<ErrorComponentProps> = ({ error }) => {
      const { instance } = useMsal();

      if (error instanceof InteractionRequiredAuthError) {
        instance.loginRedirect(authenticationRequest);
        return <div>Redirecting to login...</div>;
      }

      return (
        <div>
          {error
            ? `An error occurred: ${error.errorMessage}`
            : "An unknown error occurred."}
        </div>
      );
    };

    return (
      <MsalProvider instance={msalInstance}>
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={authenticationRequest}
          loadingComponent={LoadingComponent}
          errorComponent={ErrorComponent}
        >
          <ProtectedRouteEntraID>
            <>{children}</>
          </ProtectedRouteEntraID>
        </MsalAuthenticationTemplate>
      </MsalProvider>
    );
  }

  // No-auth fallback (AUTHENTICATION_METHOD=UNSAFE or any non-AUTH0/ENTRAID
  // value). Host-gated so an accidental UNSAFE deploy refuses traffic instead
  // of silently granting access via ?user_name=. Companion BE guard lives in
  // api/core/dependencies.py::get_current_user_unsafe.
  if (!isLocalhostHostname()) return <UnsafeAuthRefusedNotice />;
  return <UnsafeAuthProvider>{children}</UnsafeAuthProvider>;
};

export default AuthProvider;
